import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Zap, 
  TrendingUp, 
  Users, 
  MessageSquare,
  Wifi,
  WifiOff,
  RotateCcw,
  Play,
  Pause,
  RefreshCw
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface RealTimeEvent {
  id: string;
  type: 'processing' | 'notification' | 'error' | 'recovery' | 'communication';
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'error' | 'success';
  timestamp: Date;
  userId?: string;
  metadata?: any;
}

interface PerformanceMetric {
  name: string;
  value: number;
  target: number;
  unit: string;
  trend: 'up' | 'down' | 'stable';
}

interface ConnectionStatus {
  isConnected: boolean;
  lastHeartbeat: Date | null;
  reconnectAttempts: number;
  latency: number;
}

export const RealTimeProcessingCommunication: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  // State management
  const [isActive, setIsActive] = useState(false);
  const [events, setEvents] = useState<RealTimeEvent[]>([]);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetric[]>([
    { name: 'Processing Speed', value: 85, target: 90, unit: '%', trend: 'up' },
    { name: 'Error Rate', value: 2.1, target: 1.0, unit: '%', trend: 'down' },
    { name: 'Recovery Time', value: 1.2, target: 1.0, unit: 's', trend: 'stable' },
    { name: 'Throughput', value: 450, target: 500, unit: 'docs/hr', trend: 'up' }
  ]);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    isConnected: false,
    lastHeartbeat: null,
    reconnectAttempts: 0,
    latency: 0
  });
  const [activeUsers, setActiveUsers] = useState<number>(0);
  const [errorRecoveryStatus, setErrorRecoveryStatus] = useState({
    activeRecoveries: 0,
    successfulRecoveries: 0,
    failedRecoveries: 0
  });

  // Refs for cleanup
  const channelRef = useRef<any>(null);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const metricsIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize real-time connections
  useEffect(() => {
    if (!user?.id || !isActive) return;

    const setupRealTimeConnection = async () => {
      try {
        // Main processing channel
        const processingChannel = supabase
          .channel('processing-communication')
          .on('presence', { event: 'sync' }, () => {
            const state = processingChannel.presenceState();
            setActiveUsers(Object.keys(state).length);
          })
          .on('presence', { event: 'join' }, ({ key, newPresences }) => {
            addEvent({
              type: 'communication',
              title: 'User Joined',
              message: `User ${key} joined the processing session`,
              severity: 'info'
            });
          })
          .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
            addEvent({
              type: 'communication',
              title: 'User Left',
              message: `User ${key} left the processing session`,
              severity: 'info'
            });
          })
          .on('broadcast', { event: 'notification' }, (payload) => {
            addEvent({
              type: 'notification',
              title: payload.title,
              message: payload.message,
              severity: payload.severity || 'info'
            });
          })
          .on('broadcast', { event: 'error-recovery' }, (payload) => {
            handleErrorRecoveryEvent(payload);
          });

        // Database changes channel
        const dbChannel = supabase
          .channel('db-changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'bills',
              filter: `user_id=eq.${user.id}`
            },
            (payload) => {
              handleDatabaseChange(payload);
            }
          )
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'processing_logs',
              filter: `user_id=eq.${user.id}`
            },
            (payload) => {
              handleProcessingLogChange(payload);
            }
          );

        // Subscribe to channels
        await processingChannel.subscribe();
        await dbChannel.subscribe();

        // Track user presence
        await processingChannel.track({
          user_id: user.id,
          online_at: new Date().toISOString(),
          status: 'active'
        });

        channelRef.current = { processingChannel, dbChannel };
        setConnectionStatus(prev => ({ ...prev, isConnected: true }));

        // Start heartbeat
        startHeartbeat();
        
        addEvent({
          type: 'communication',
          title: 'Connected',
          message: 'Real-time communication established',
          severity: 'success'
        });

      } catch (error) {
        console.error('Failed to setup real-time connection:', error);
        setConnectionStatus(prev => ({ 
          ...prev, 
          isConnected: false,
          reconnectAttempts: prev.reconnectAttempts + 1
        }));
        
        addEvent({
          type: 'error',
          title: 'Connection Failed',
          message: 'Failed to establish real-time connection',
          severity: 'error'
        });
      }
    };

    setupRealTimeConnection();

    return () => {
      cleanup();
    };
  }, [user?.id, isActive]);

  // Performance metrics simulation
  useEffect(() => {
    if (!isActive) return;

    metricsIntervalRef.current = setInterval(() => {
      setPerformanceMetrics(prev => prev.map(metric => ({
        ...metric,
        value: Math.max(0, metric.value + (Math.random() - 0.5) * 10),
        trend: Math.random() > 0.5 ? 'up' : Math.random() > 0.25 ? 'down' : 'stable'
      })));
    }, 3000);

    return () => {
      if (metricsIntervalRef.current) {
        clearInterval(metricsIntervalRef.current);
      }
    };
  }, [isActive]);

  const startHeartbeat = () => {
    heartbeatRef.current = setInterval(async () => {
      const start = Date.now();
      try {
        const { error } = await supabase.from('bills').select('count').limit(1);
        if (!error) {
          const latency = Date.now() - start;
          setConnectionStatus(prev => ({
            ...prev,
            lastHeartbeat: new Date(),
            latency
          }));
        }
      } catch (error) {
        console.error('Heartbeat failed:', error);
        setConnectionStatus(prev => ({ 
          ...prev, 
          isConnected: false 
        }));
      }
    }, 5000);
  };

  const cleanup = () => {
    if (channelRef.current) {
      channelRef.current.processingChannel?.unsubscribe();
      channelRef.current.dbChannel?.unsubscribe();
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
    }
    if (metricsIntervalRef.current) {
      clearInterval(metricsIntervalRef.current);
    }
  };

  const addEvent = (eventData: Omit<RealTimeEvent, 'id' | 'timestamp'>) => {
    const newEvent: RealTimeEvent = {
      ...eventData,
      id: Date.now().toString(),
      timestamp: new Date()
    };

    setEvents(prev => [newEvent, ...prev].slice(0, 100));

    // Show toast for important events
    if (eventData.severity === 'error' || eventData.severity === 'warning') {
      toast({
        title: eventData.title,
        description: eventData.message,
        variant: eventData.severity === 'error' ? 'destructive' : 'default'
      });
    }
  };

  const handleDatabaseChange = (payload: any) => {
    const eventType = payload.eventType;
    const table = payload.table;
    
    addEvent({
      type: 'processing',
      title: `${table} ${eventType}`,
      message: `Database change detected: ${eventType} on ${table}`,
      severity: 'info'
    });
  };

  const handleProcessingLogChange = (payload: any) => {
    if (payload.new?.status === 'error') {
      triggerErrorRecovery(payload.new);
    }
    
    addEvent({
      type: 'processing',
      title: 'Processing Update',
      message: `Processing status: ${payload.new?.status || 'unknown'}`,
      severity: payload.new?.status === 'error' ? 'error' : 'info'
    });
  };

  const handleErrorRecoveryEvent = (payload: any) => {
    setErrorRecoveryStatus(prev => ({
      ...prev,
      activeRecoveries: payload.type === 'start' ? prev.activeRecoveries + 1 : Math.max(0, prev.activeRecoveries - 1),
      successfulRecoveries: payload.success ? prev.successfulRecoveries + 1 : prev.successfulRecoveries,
      failedRecoveries: payload.success === false ? prev.failedRecoveries + 1 : prev.failedRecoveries
    }));

    addEvent({
      type: 'recovery',
      title: payload.title || 'Error Recovery',
      message: payload.message || 'Error recovery process triggered',
      severity: payload.success ? 'success' : payload.success === false ? 'error' : 'info'
    });
  };

  const triggerErrorRecovery = async (errorData: any) => {
    try {
      // Broadcast error recovery start
      if (channelRef.current?.processingChannel) {
        await channelRef.current.processingChannel.send({
          type: 'broadcast',
          event: 'error-recovery',
          payload: {
            type: 'start',
            errorId: errorData.id,
            title: 'Error Recovery Started',
            message: `Attempting to recover from error: ${errorData.error_message || 'Unknown error'}`
          }
        });
      }

      // Simulate error recovery process
      setTimeout(async () => {
        const success = Math.random() > 0.3; // 70% success rate
        
        if (channelRef.current?.processingChannel) {
          await channelRef.current.processingChannel.send({
            type: 'broadcast',
            event: 'error-recovery',
            payload: {
              type: 'complete',
              errorId: errorData.id,
              success,
              title: success ? 'Recovery Successful' : 'Recovery Failed',
              message: success ? 'Error has been automatically resolved' : 'Manual intervention required'
            }
          });
        }
      }, 2000);

    } catch (error) {
      console.error('Error recovery failed:', error);
    }
  };

  const toggleRealTime = () => {
    setIsActive(!isActive);
    if (!isActive) {
      addEvent({
        type: 'communication',
        title: 'Real-time Activated',
        message: 'Real-time processing communication started',
        severity: 'success'
      });
    } else {
      cleanup();
      setConnectionStatus({
        isConnected: false,
        lastHeartbeat: null,
        reconnectAttempts: 0,
        latency: 0
      });
      addEvent({
        type: 'communication',
        title: 'Real-time Deactivated',
        message: 'Real-time processing communication stopped',
        severity: 'info'
      });
    }
  };

  const reconnect = async () => {
    setConnectionStatus(prev => ({ 
      ...prev, 
      reconnectAttempts: prev.reconnectAttempts + 1 
    }));
    setIsActive(false);
    setTimeout(() => setIsActive(true), 1000);
  };

  const broadcastNotification = async (title: string, message: string, severity: 'info' | 'warning' | 'error' | 'success' = 'info') => {
    if (channelRef.current?.processingChannel) {
      await channelRef.current.processingChannel.send({
        type: 'broadcast',
        event: 'notification',
        payload: { title, message, severity }
      });
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'processing': return <Activity className="h-4 w-4" />;
      case 'notification': return <MessageSquare className="h-4 w-4" />;
      case 'error': return <XCircle className="h-4 w-4" />;
      case 'recovery': return <RotateCcw className="h-4 w-4" />;
      case 'communication': return <Users className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error': return 'destructive';
      case 'warning': return 'secondary';
      case 'success': return 'default';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Real-time Processing & Communication</h2>
          <p className="text-muted-foreground">
            Monitor live processing, manage notifications, and handle error recovery
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {connectionStatus.isConnected ? (
              <Wifi className="h-4 w-4 text-green-500" />
            ) : (
              <WifiOff className="h-4 w-4 text-red-500" />
            )}
            <span className="text-sm text-muted-foreground">
              {connectionStatus.isConnected ? `${connectionStatus.latency}ms` : 'Disconnected'}
            </span>
          </div>
          
          {!connectionStatus.isConnected && isActive && (
            <Button variant="outline" size="sm" onClick={reconnect}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reconnect
            </Button>
          )}
          
          <Button
            onClick={toggleRealTime}
            variant={isActive ? "secondary" : "default"}
            className="flex items-center gap-2"
          >
            {isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {isActive ? 'Stop' : 'Start'} Real-time
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Active Users</p>
                <p className="text-2xl font-bold">{activeUsers}</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Active Recoveries</p>
                <p className="text-2xl font-bold">{errorRecoveryStatus.activeRecoveries}</p>
              </div>
              <RotateCcw className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Success Rate</p>
                <p className="text-2xl font-bold">
                  {errorRecoveryStatus.successfulRecoveries + errorRecoveryStatus.failedRecoveries > 0
                    ? Math.round((errorRecoveryStatus.successfulRecoveries / (errorRecoveryStatus.successfulRecoveries + errorRecoveryStatus.failedRecoveries)) * 100)
                    : 0}%
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Events</p>
                <p className="text-2xl font-bold">{events.length}</p>
              </div>
              <Activity className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="events" className="space-y-4">
        <TabsList>
          <TabsTrigger value="events">Live Events</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="communication">Communication</TabsTrigger>
          <TabsTrigger value="recovery">Error Recovery</TabsTrigger>
        </TabsList>

        <TabsContent value="events" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Real-time Event Stream</CardTitle>
              <CardDescription>
                Live updates from processing, notifications, and system events
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-96">
                <div className="space-y-2">
                  {events.map((event) => (
                    <div
                      key={event.id}
                      className="flex items-start gap-3 p-3 rounded-lg border bg-background"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {getEventIcon(event.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium truncate">{event.title}</p>
                          <div className="flex items-center gap-2">
                            <Badge variant={getSeverityColor(event.severity)}>
                              {event.severity}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {event.timestamp.toLocaleTimeString()}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">{event.message}</p>
                      </div>
                    </div>
                  ))}
                  {events.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No events yet. Start real-time monitoring to see live updates.
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {performanceMetrics.map((metric) => (
              <Card key={metric.name}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{metric.name}</CardTitle>
                    <Badge variant="outline">
                      {metric.trend === 'up' ? '↗' : metric.trend === 'down' ? '↘' : '→'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold">
                        {metric.value.toFixed(1)}{metric.unit}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        Target: {metric.target}{metric.unit}
                      </span>
                    </div>
                    <Progress 
                      value={(metric.value / metric.target) * 100} 
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="communication" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Team Communication</CardTitle>
              <CardDescription>
                Broadcast notifications and coordinate with team members
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => broadcastNotification('Processing Update', 'System running smoothly', 'success')}
                >
                  Send Status Update
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => broadcastNotification('Maintenance Alert', 'Scheduled maintenance in 30 minutes', 'warning')}
                >
                  Send Maintenance Alert
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => broadcastNotification('Priority Alert', 'High priority processing queue detected', 'error')}
                >
                  Send Priority Alert
                </Button>
              </div>
              
              <Alert>
                <MessageSquare className="h-4 w-4" />
                <AlertDescription>
                  Broadcast messages will be sent to all active users in this processing session.
                  Use responsibly to maintain effective team communication.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recovery" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Error Recovery Dashboard</CardTitle>
              <CardDescription>
                Monitor and manage automatic error recovery processes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {errorRecoveryStatus.successfulRecoveries}
                  </div>
                  <div className="text-sm text-muted-foreground">Successful</div>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-red-600">
                    {errorRecoveryStatus.failedRecoveries}
                  </div>
                  <div className="text-sm text-muted-foreground">Failed</div>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {errorRecoveryStatus.activeRecoveries}
                  </div>
                  <div className="text-sm text-muted-foreground">Active</div>
                </div>
              </div>
              
              <Button 
                variant="outline" 
                onClick={() => triggerErrorRecovery({ id: 'test', error_message: 'Test recovery process' })}
                className="w-full"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Test Error Recovery
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};